import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    AppBar,
    Box,
    Toolbar,
    Drawer,
    Button,
    Menu,
    MenuItem,
    ListItem,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import PeopleIcon from "@mui/icons-material/People";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import OrdersIcon from "@mui/icons-material/ShoppingCart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";
import CountryIcon from "@mui/icons-material/Flag";
import InvoiceIcon from "@mui/icons-material/Receipt";
import MessageIcon from "@mui/icons-material/Message";
import ServiceIcon from "@mui/icons-material/Rule";
import ReportsIcon from "@mui/icons-material/BarChart";
import OrderTypeIcon from "@mui/icons-material/MergeType";
import OrderStateIcon from "@mui/icons-material/FilterAlt";
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import TaxTypeMappingIcon from "@mui/icons-material/TextFields";
import { API_BASE_URL } from "../config.ts";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
//import logo from '../assets/logo.png';
import logo from '../assets/DACustomsBlueGreenSmall.png';

import axiosExt from '../axios';

interface LayoutProps {
    children: React.ReactNode;
}

const drawerWidth = 240;

const LayoutAdmin: React.FC<LayoutProps> = ({ children }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const roles: string[] = JSON.parse(localStorage.getItem("roles") || "[]");
    const isAdmin = roles.includes("admin");

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("roles");
        navigate("/");
    };

    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(localStorage.getItem("language") || "en");


    const adminMenuItems = [
        { text: t("Accounting Translations"), icon: <ServiceIcon />, path: "/accountingtranslations" },
        { text: t("Countries"), icon: <CountryIcon />, path: "/countries" },
        { text: t("Email Connection"), icon: <EmailIcon />, path: "/emailconnection" },
        { text: t("Services"), icon: <ServiceIcon />, path: "/services" },
        { text: t("Types"), icon: <OrderTypeIcon />, path: "/ordertypes" },
        { text: t("Order States"), icon: <OrderStateIcon />, path: "/orderstates" },
        { text: t("Detail States"), icon: <OrderStateIcon />, path: "/detailstates" },
        { text: t("Tax Type Mappings"), icon: <TaxTypeMappingIcon />, path: "/taxtypemappings" },
        { text: t("Ignore List"), icon: <DoNotDisturbOnIcon />, path: "/ignorelist" },
        { text: t("Message Templates"), icon: <MessageIcon />, path: "/messageTemplates" },
        ...(isAdmin ? [{ text: t("Users"), icon: <AdminPanelSettingsIcon />, path: "/users" }] : []),
        { text: t("Logout"), icon: <LogoutIcon />, action: handleLogout },
    ];

    const [menuAnchor, setMenuAnchor] = useState<null | { anchor: HTMLElement, menu: string }>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, menuName: string) => {
        setMenuAnchor({ anchor: event.currentTarget, menu: menuName });
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    useEffect(() => {
        const userLang = localStorage.getItem("language") || "en";
        if (localStorage.getItem("token")) {
            setSelectedLanguage(userLang);
            i18n.changeLanguage(userLang);
        };

    }, []);

    const handleLanguageChange = async (lang: string) => {
        setSelectedLanguage(lang);
        i18n.changeLanguage(lang);
        localStorage.setItem("language", lang);

        if (localStorage.getItem("token")) {
            try {
                await axiosExt.post(
                    `${API_BASE_URL}/users/set-language/${localStorage.getItem("userId")}`,
                    { language: lang },
                    {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    }
                );
            } catch (error) {
                console.error("Error updating language in API", error);
            }
        }

        setLanguageMenuAnchor(null);
    };



    return (
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
            {/* Side Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" },
                }}
            >
                <Toolbar />
                <Box sx={{ overflow: "auto" }}>
                    <List>
                        {adminMenuItems.map((item, index) => (
                            <ListItem
                                key={index}
                                disablePadding
                                onClick={() =>
                                    item.path ? navigate(item.path) : item.action?.()
                                }
                            >
                                <ListItemButton>
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                </Box>
            </Drawer>



            {/* Main Content Section */}
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, width:"100%" }}>
                {/* Top Navigation Bar */}
                <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor:"#0F0F10" }}>
                        {/* App Title */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src={logo}
                                alt="DA Customs2"
                                style={{ height: 40, marginRight: 16, flexGrow: 1, objectFit: "contain" }}
                            />
                        </Box>

                        {/* Main Menu Items */}
                        {/*<Button color="inherit" startIcon={<OrdersIcon />} onClick={(e) => handleMenuOpen(e, "orders")}>*/}
                        {/*    {t("Orders")}*/}
                        {/*</Button>*/}

                        <Box sx={{ display: 'flex', gap: 1 }}>

                            <Button color="inherit" onClick={() => navigate("/orderdetailspage?view=grid")} startIcon={<OrdersIcon />}>
                                {t("Orders")}
                            </Button>

                            <Button color="inherit" onClick={() => navigate("/orderdetailspage?view=list")} startIcon={<VisibilityIcon />}>
                                {t("Details")}
                            </Button>


                            <Button color="inherit" onClick={() => navigate("/customers")} startIcon={<PeopleIcon />}>
                                {t("Customers")}
                            </Button>
                            <Button color="inherit" onClick={() => navigate("/contacts")} startIcon={<ContactPhoneIcon />}>
                                {t("Contacts")}
                            </Button>
                            <Button color="inherit" onClick={() => navigate("/emails")} startIcon={<EmailIcon />}>
                                {t("Emails")}
                            </Button>
                            <Button color="inherit" onClick={() => navigate("/invoices")} startIcon={<InvoiceIcon />}>
                                {t("Invoices")}
                            </Button>
                            {isAdmin && (
                                <>
                                    <Button color="inherit" startIcon={<ReportsIcon />} onClick={(e) => handleMenuOpen(e, "reports")}>
                                        {t("Reports")}
                                    </Button>


                                </>
                            )}

                            {/* Admin Dropdown Menu */}
                            {isAdmin && (
                                <>
                                    <Button color="inherit" startIcon={<AdminPanelSettingsIcon />} onClick={(e) => handleMenuOpen(e, "admin")}>
                                        {t("Admin")}
                                    </Button>

                                </>
                            )}

                            <List>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}>
                                        <ListItemIcon><TranslateIcon sx={{ color: 'inherit' }} /></ListItemIcon>
                                        <ListItemText primary={`${selectedLanguage.toUpperCase()}`} />
                                    </ListItemButton>
                                </ListItem>
                            </List>

                            <Menu
                                anchorEl={menuAnchor?.anchor || null}
                                open={Boolean(menuAnchor)}
                                onClose={handleMenuClose}
                            >
                                {menuAnchor?.menu === "orders" && (
                                    <>
                                        <MenuItem onClick={() => { navigate("/orderdetailspage?view=list"); handleMenuClose(); }}>{t("Orders Details")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/orderdetailspage?view=grid"); handleMenuClose(); }}>{t("Orders Table")}</MenuItem>
                                    </>
                                )}
                                {menuAnchor?.menu === "reports" && (
                                    <>
                                        <MenuItem onClick={() => { navigate("/reportordersstatus"); handleMenuClose(); }}>{t("Orders Status")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/reportorderstatusbyassigned"); handleMenuClose(); }}>{t("Status By Person")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/surveys"); handleMenuClose(); }}>{t("Survey Results")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/reportcustomerordersno"); handleMenuClose(); }}>{t("Orders Per Customer")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/reportnps"); handleMenuClose(); }}>{t("Customer NPS")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/reportreminders"); handleMenuClose(); }}>{t("Reminders")}</MenuItem>
                                    </>
                                )}
                                {menuAnchor?.menu === "admin" && (
                                    <>
                                        <MenuItem onClick={() => { navigate("/accountingtranslations"); handleMenuClose(); }}>{t("Accounting Translations")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/countries"); handleMenuClose(); }}>{t("Countries")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/emailconnection"); handleMenuClose(); }}>{t("Email Connection")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/services"); handleMenuClose(); }}>{t("Services")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/ordertypes"); handleMenuClose(); }}>{t("Types")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/orderstates"); handleMenuClose(); }}>{t("Order States")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/detailstates"); handleMenuClose(); }}>{t("Detail States")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/taxtypemappings"); handleMenuClose(); }}>{t("Tax Type Mappings")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/ignorelist"); handleMenuClose(); }}>{t("Ignore List")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/users"); handleMenuClose(); }}>{t("Users")}</MenuItem>
                                        <MenuItem onClick={() => { navigate("/messageTemplates"); handleMenuClose(); }}>{t("Message Templates")}</MenuItem>
                                    </>
                                )}
                            </Menu>
                            {/* Language Menu */}
                            <Menu
                                anchorEl={languageMenuAnchor}
                                open={Boolean(languageMenuAnchor)}
                                onClose={() => setLanguageMenuAnchor(null)}
                            >
                                <MenuItem onClick={() => handleLanguageChange("en")}>English (EN)</MenuItem>
                                <MenuItem onClick={() => handleLanguageChange("de")}>Deutsch (DE)</MenuItem>
                                <MenuItem onClick={() => handleLanguageChange("pl")}>Polski (PL)</MenuItem>
                                <MenuItem onClick={() => handleLanguageChange("ro")}>Romana (RO)</MenuItem>
                            </Menu>

                            <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
                                Logout
                            </Button>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Content Area */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        width: `calc(100vw - ${drawerWidth}px)`, // use viewport width, not 100%
                        minHeight: "calc(100vw - 80px)",
                        backgroundColor: "#fafafa",
                        p: 3,
                        mt: 8,
                        overflow: "hidden",
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );

};

export default LayoutAdmin;
